import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import SupplierService from "../../services/SupplierService";

const paymentAllocationMethods = [
  {
    label: "Otomatik dağıt — En eski vadeden başla",
    value: 1,
    description:
      "Tedarikçiye ödeme girildiğinde sistem açık borçları vade tarihine göre otomatik kapatır.",
  },
  {
    label: "Manuel dağıt — Ödeme sırasında ben seçeyim",
    value: 2,
    description:
      "Ödeme kaydederken hangi alış/vade borçlarının kapatılacağını kullanıcı belirler.",
  },
];

const IconBox = ({
  icon,
  active = false,
  dark = false,
  slate = false,
  size = 36,
  iconSize = 15,
}) => {
  let bgColor = "#ecfdf5";
  let borderColor = "#d1fae5";
  let iconColor = "#059669";

  if (dark) {
    bgColor = "rgba(255,255,255,0.10)";
    borderColor = "rgba(255,255,255,0.15)";
    iconColor = "#ffffff";
  }

  if (active) {
    bgColor = "#059669";
    borderColor = "#059669";
    iconColor = "#ffffff";
  }

  if (slate) {
    bgColor = "#ffffff";
    borderColor = "#e2e8f0";
    iconColor = "#334155";
  }

  return (
    <div
      className="flex items-center justify-center shrink-0"
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius: 10,
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
      }}
    >
      <i
        className={icon}
        style={{
          fontSize: iconSize,
          color: iconColor,
        }}
      />
    </div>
  );
};

const SectionCard = ({ icon, title, description, children }) => {
  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <IconBox icon={icon} size={36} iconSize={15} />

          <div>
            <h2 className="text-[18px] font-black text-slate-900 leading-none">
              {title}
            </h2>
            <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">
              {description}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">{children}</div>
    </div>
  );
};

const FieldLabel = ({ icon, children }) => {
  return (
    <label className="flex items-center gap-2 text-[12px] font-black text-slate-500 uppercase tracking-widest mb-2">
      {icon && (
        <i
          className={icon}
          style={{
            fontSize: 13,
            color: "#10b981",
          }}
        />
      )}
      {children}
    </label>
  );
};

export default function SuppliersEditPage() {
  const { id } = useParams();
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    taxNumber: "",
    taxOffice: "",
    phoneNumber: "",
    email: "",
    bankInfo: "",
    address: "",
    openingBalance: 0,
    recordStatus: 1,
    useDueTracking: false,
    defaultDueDay: 30,
    paymentAllocationMethod: 1,
  });
  const [isSaving, setIsSaving] = useState(false);

  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
    loadSupplier();
  }, [id]);

  const loadSupplier = async () => {
    try {
      const data = await SupplierService.getSupplierById(id);
      setForm({
        id: id,
        name: data.name || "",
        taxNumber: data.taxNumber || "",
        taxOffice: data.taxOffice || "",
        phoneNumber: data.phoneNumber || "",
        email: data.email || "",
        bankInfo: data.bankInfo || "",
        address: data.address || data.adress || "",
        openingBalance: data.openingBalance || 0,
        recordStatus: data.recordStatus ?? 1,
        useDueTracking: data.useDueTracking || false,
        defaultDueDay: data.defaultDueDay || 30,
        paymentAllocationMethod: data.paymentAllocationMethod || 1,
      });
    } catch (err) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail: "Tedarikçi bilgileri yüklenemedi.",
      });
      setTimeout(() => navigate("/suppliers"), 2000);
    } finally {
      setLoading(false);
    }
  };

  const goToListPage = () => navigate("/suppliers");

  const setField = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDueTrackingChange = (value) => {
    setForm((prev) => ({
      ...prev,
      useDueTracking: value,
      defaultDueDay: value ? prev.defaultDueDay ?? 30 : 30,
      paymentAllocationMethod: value ? prev.paymentAllocationMethod || 1 : 1,
    }));
  };

  const paymentAllocationItemTemplate = (option) => {
    return (
      <div className="flex items-start gap-3 py-1">
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: 30,
            height: 30,
            minWidth: 30,
            minHeight: 30,
            borderRadius: 8,
            backgroundColor: "#ecfdf5",
            border: "1px solid #d1fae5",
          }}
        >
          <i
            className={
              option.value === 1 ? "pi pi-sort-amount-down" : "pi pi-list-check"
            }
            style={{
              fontSize: 13,
              color: "#059669",
            }}
          />
        </div>

        <div>
          <div className="text-[14px] font-bold text-slate-800">
            {option.label}
          </div>
          <div className="text-[12px] text-slate-500 mt-1 leading-relaxed">
            {option.description}
          </div>
        </div>
      </div>
    );
  };

  const selectedPaymentAllocationTemplate = (option, props) => {
    if (!option) {
      return <span>{props.placeholder}</span>;
    }

    return (
      <div className="flex items-center gap-2">
        <i
          className={
            option.value === 1 ? "pi pi-sort-amount-down" : "pi pi-list-check"
          }
          style={{
            fontSize: 13,
            color: "#059669",
          }}
        />
        <span className="text-[14px] font-semibold">{option.label}</span>
      </div>
    );
  };

  const validateForm = () => {
    if (!form.name?.trim()) {
      toast.current.show({
        severity: "warn",
        summary: "Eksik Bilgi",
        detail: "Tedarikçi adı zorunludur.",
        life: 3000,
      });
      return false;
    }

    if (
      form.useDueTracking &&
      (!form.defaultDueDay || form.defaultDueDay <= 0)
    ) {
      toast.current.show({
        severity: "warn",
        summary: "Eksik Bilgi",
        detail: "Vade takibi açıkken varsayılan vade günü girilmelidir.",
        life: 3500,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSaving(true);

    const payload = {
      id: id,
      supplier: {
        Id: id,
        Name: form.name,
        TaxNumber: form.taxNumber,
        TaxOffice: form.taxOffice,
        PhoneNumber: form.phoneNumber,
        Email: form.email,
        BankInfo: form.bankInfo,
        Address: form.address,
        OpeningBalance: form.openingBalance ?? 0,
        RecordStatus: form.recordStatus,
        UseDueTracking: form.useDueTracking,
        DefaultDueDay: form.useDueTracking ? form.defaultDueDay : null,
        PaymentAllocationMethod: form.useDueTracking
          ? form.paymentAllocationMethod
          : 1,
      },
    };

    try {
      const res = await SupplierService.updateSupplier(payload);

      toast.current.show({
        severity: "success",
        summary: "Başarılı",
        detail: res?.message || "Tedarikçi başarıyla güncellendi.",
        life: 3000,
      });

      setTimeout(() => navigate("/suppliers"), 1200);
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Hata",
        detail:
          error.response?.data?.message ||
          error.message ||
          "Tedarikçi güncellenirken bir hata oluştu.",
        life: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <i className="pi pi-spin pi-spinner text-4xl text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] p-2 md:p-2">
      <Toast ref={toast} appendTo={mounted ? document.body : "self"} />

      <div className="max-w-7xl mx-auto space-y-3">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-slate-900 to-emerald-900 p-6 md:p-8 shadow-lg">
          <div
            className="absolute rounded-full bg-emerald-400/10"
            style={{
              right: -40,
              top: -40,
              width: 192,
              height: 192,
            }}
          />

          <div
            className="absolute rounded-full bg-white/5"
            style={{
              right: 80,
              bottom: 0,
              width: 128,
              height: 128,
            }}
          />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="flex items-start gap-3">
              <IconBox icon="pi pi-pencil" dark size={40} iconSize={17} />

              <div>
                <div className="text-[12px] font-black tracking-[0.22em] text-emerald-300 uppercase">
                  Tedarikçi Yönetimi
                </div>
                <h1 className="text-2xl md:text-4xl font-black text-white mt-2">
                  Tedarikçiyi Düzenle
                </h1>
                <p className="text-[14px] md:text-[15px] text-slate-300 mt-3 max-w-2xl leading-relaxed">
                  Tedarikçi bilgilerini, bakiyesini ve vade takip ayarlarını
                  güncelleyin.
                </p>
              </div>
            </div>

            <Button
              type="button"
              label="Tedarikçi Listesi"
              icon="pi pi-arrow-left"
              outlined
              className="!rounded-2xl !text-white !border-white/30 hover:!bg-white/10"
              onClick={goToListPage}
            />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <SectionCard
            icon="pi pi-building"
            title="Firma Bilgileri"
            description="Tedarikçinin resmi adı, vergi bilgileri ve iletişim detayları."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <FieldLabel icon="pi pi-briefcase">Tedarikçi Adı</FieldLabel>
                <InputText
                  name="name"
                  value={form.name}
                  onChange={handleInputChange}
                  placeholder="Ör: Aydınlar Ltd. Şti."
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-map-marker">Vergi Dairesi</FieldLabel>
                <InputText
                  name="taxOffice"
                  value={form.taxOffice}
                  onChange={handleInputChange}
                  placeholder="Ör: Muratpaşa"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-id-card">
                  Vergi Numarası / T.C. No
                </FieldLabel>
                <InputText
                  name="taxNumber"
                  value={form.taxNumber}
                  onChange={handleInputChange}
                  maxLength={11}
                  placeholder="Ör: 1234567890"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-phone">Telefon</FieldLabel>
                <InputMask
                  name="phoneNumber"
                  mask="(999) 999 99 99"
                  value={form.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="(555) 123 45 67"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-envelope">E-posta</FieldLabel>
                <InputText
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  type="email"
                  placeholder="tedarikci@eposta.com"
                  className="w-full !h-12 !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-check-circle">Durum</FieldLabel>
                <Dropdown
                  value={form.recordStatus}
                  options={[
                    { label: "Aktif", value: 1 },
                    { label: "Pasif", value: 0 },
                  ]}
                  onChange={(e) => setField("recordStatus", e.value)}
                  className="w-full !h-12 !rounded-xl !text-[15px] flex items-center"
                />
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon="pi pi-wallet"
            title="Cari ve Borç Takibi"
            description="Açılış bakiyesi, vade takip tercihi ve ödeme kapama ayarları."
          >
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
              <div className="xl:col-span-4">
                <div className="h-full rounded-3xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <IconBox
                      icon="pi pi-money-bill"
                      slate
                      size={36}
                      iconSize={15}
                    />

                    <div>
                      <div className="text-[15px] font-black text-slate-900">
                        Açılış Bakiyesi
                      </div>
                      <div className="text-[13px] text-slate-500">
                        İlk cari borç/alacak durumu
                      </div>
                    </div>
                  </div>

                  <InputNumber
                    value={form.openingBalance}
                    onValueChange={(e) =>
                      setField("openingBalance", e.value ?? 0)
                    }
                    mode="currency"
                    currency="TRY"
                    locale="tr-TR"
                    className="w-full"
                    inputClassName="!h-12 !rounded-xl !text-[15px]"
                  />

                  <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-4">
                    <div className="flex items-start gap-3">
                      <i className="pi pi-info-circle text-amber-500 mt-0.5" />
                      <p className="text-[13px] text-slate-600 leading-relaxed">
                        Tedarikçiye borcunuz varsa pozitif, tedarikçi size
                        borçluysa eksi değer girin.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="xl:col-span-8">
                <div
                  className={`h-full rounded-3xl border p-5 transition-all ${
                    form.useDueTracking
                      ? "border-emerald-200 bg-emerald-50/70"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <IconBox
                        icon="pi pi-calendar-clock"
                        active={form.useDueTracking}
                        slate={!form.useDueTracking}
                        size={36}
                        iconSize={15}
                      />

                      <div>
                        <div className="text-[17px] font-black text-slate-900">
                          Vade takipli borç takibi
                        </div>
                        <p className="text-[14px] text-slate-600 mt-2 leading-relaxed">
                          Bu özellik açıkken alış işlemlerinde vade tarihi
                          önerilir. Ödeme yapılırken açık borç kalemleri
                          kapatılarak raporlarda vadesi gelen/geçen borçlar
                          takip edilebilir.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 bg-white rounded-2xl border border-slate-200 px-4 py-3">
                      <span className="text-[13px] font-bold text-slate-600">
                        {form.useDueTracking ? "Aktif" : "Pasif"}
                      </span>
                      <InputSwitch
                        checked={form.useDueTracking}
                        onChange={(e) => handleDueTrackingChange(e.value)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
                    <div>
                      <FieldLabel icon="pi pi-clock">
                        Varsayılan Vade Günü
                      </FieldLabel>
                      <InputNumber
                        value={form.defaultDueDay}
                        onValueChange={(e) =>
                          setField("defaultDueDay", e.value)
                        }
                        min={1}
                        max={365}
                        disabled={!form.useDueTracking}
                        suffix=" gün"
                        placeholder="Ör: 30"
                        className="w-full"
                        inputClassName="!h-12 !rounded-xl !text-[15px]"
                      />
                    </div>

                    <div>
                      <FieldLabel icon="pi pi-sliders-h">
                        Varsayılan Ödeme Dağıtımı
                      </FieldLabel>

                      <Dropdown
                        value={form.paymentAllocationMethod}
                        options={paymentAllocationMethods}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(e) => setField("paymentAllocationMethod", e.value)}
                        disabled={!form.useDueTracking}
                        appendTo={mounted ? document.body : "self"}
                        className="w-full !rounded-xl"
                        placeholder="Ödeme dağıtım yöntemini seçiniz"
                        itemTemplate={paymentAllocationItemTemplate}
                        valueTemplate={selectedPaymentAllocationTemplate}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            icon="pi pi-credit-card"
            title="Banka ve Adres Bilgileri"
            description="IBAN, banka açıklaması, sevkiyat veya iletişim adresi gibi ek bilgiler."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <FieldLabel icon="pi pi-credit-card">Banka Bilgisi</FieldLabel>
                <InputTextarea
                  name="bankInfo"
                  value={form.bankInfo}
                  onChange={handleInputChange}
                  rows={5}
                  autoResize
                  placeholder="Banka adı, IBAN veya açıklama"
                  className="w-full !rounded-xl !text-[15px]"
                />
              </div>

              <div>
                <FieldLabel icon="pi pi-home">Adres</FieldLabel>
                <InputTextarea
                  name="address"
                  value={form.address}
                  onChange={handleInputChange}
                  rows={5}
                  autoResize
                  placeholder="Adres bilgisi"
                  className="w-full !rounded-xl !text-[15px]"
                />
              </div>
            </div>
          </SectionCard>

          <div className="sticky bottom-0 z-20 -mx-4 md:-mx-7 px-4 md:px-7 py-4 bg-white/85 backdrop-blur-xl border-t border-slate-200">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 text-[13px] text-slate-500">
                <i className="pi pi-shield text-emerald-500" />
                <span>
                  Güncellemeler kaydedildikten sonra tüm işlemlerde geçerli olacaktır.
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  label="Vazgeç"
                  icon="pi pi-times"
                  outlined
                  className="!rounded-2xl !h-12 !px-5"
                  onClick={goToListPage}
                  disabled={isSaving}
                />

                <Button
                  type="submit"
                  label={isSaving ? "Kaydediliyor..." : "Tedarikçiyi Güncelle"}
                  icon="pi pi-save"
                  className="!rounded-2xl !h-12 !px-6 !bg-emerald-600 !border-emerald-600"
                  loading={isSaving}
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
